import {
  BellAlertIcon,
  BuildingStorefrontIcon,
  ChartBarIcon,
  CogIcon,
  CreditCardIcon,
  DevicePhoneMobileIcon,
  HomeIcon,
  TruckIcon,
  UserGroupIcon,
  UsersIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { motion } from 'framer-motion';
import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';


const navigationItems = [
  {
    name: 'Dashboard',
    href: '/',
    icon: HomeIcon,
    description: 'Overview & Analytics'
  },
  {
    name: 'Agent Management',
    href: '/agents',
    icon: UsersIcon,
    description: 'Manage agents & approvals',
    badge: 'New'
  },
  {
    name: 'Sellers Management',
    href: '/sellers',
    icon: BuildingStorefrontIcon,
    description: 'Agent-created & Individual'
  },
  {
    name: 'Cars Management',
    href: '/cars',
    icon: TruckIcon,
    description: 'All car listings'
  },
  {
    name: 'Buyers Management',
    href: '/buyers',
    icon: UserGroupIcon,
    description: 'Registered buyers'
  },
  {
    name: 'Membership Management',
    href: '/memberships',
    icon: CreditCardIcon,
    description: 'Agent & seller plans'
  },
  {
    name: 'Team Management',
    href: '/team',
    icon: UserGroupIcon,
    description: 'Admin access levels'
  },
  {
    name: 'Platform Oversight',
    href: '/oversight',
    icon: ChartBarIcon,
    description: 'Sales, leads & usage'
  },
  {
    name: 'Employee Monitoring',
    href: '/employees',
    icon: UserGroupIcon,
    description: 'Agency staff activity'
  },
  {
    name: 'Promo Codes',
    href: '/coupons',
    icon: CreditCardIcon,
    description: 'Trials & discounts'
  },
  {
    name: 'Notifications',
    href: '/notifications',
    icon: BellAlertIcon,
    description: 'Push broadcasts to apps'
  },
  {
    name: 'Analytics',
    href: '/analytics',
    icon: ChartBarIcon,
    description: 'Reports & insights'
  },
  {
    name: 'App Settings',
    href: '/settings',
    icon: DevicePhoneMobileIcon,
    description: 'Buyer & seller apps',
    submenu: [
      { name: 'Buyer App', href: '/settings/buyer-app' },
      { name: 'Seller App', href: '/settings/seller-app' }
    ]
  }
];

const Sidebar = ({ setSidebarOpen }) => {
  const location = useLocation();
  const [expandedMenus, setExpandedMenus] = useState({});

  const isActiveRoute = (href) => {
    if (href === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(href);
  };

  const toggleSubmenu = (itemName) => {
    setExpandedMenus(prev => ({
      ...prev,
      [itemName]: !prev[itemName]
    }));
  };

  const renderNavItem = (item) => {
    const isActive = isActiveRoute(item.href);
    const hasSubmenu = item.submenu && item.submenu.length > 0;
    const isExpanded = expandedMenus[item.name];

    return (
      <div key={item.name}>
        <Link to={!hasSubmenu ? item.href : '#'} onClick={(e) => {
          if (hasSubmenu) {
            e.preventDefault();
            toggleSubmenu(item.name);
          }
        }}>
          <motion.div
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className={`
              relative flex items-center px-4 py-3 text-sm font-medium rounded-xl transition-all duration-200 group
              ${isActive
                ? 'text-primary-600 bg-primary-50 shadow-sm border border-primary-100'
                : 'text-gray-600 hover:text-primary-600 hover:bg-primary-50'
              }
            `}
          >
            {isActive && !hasSubmenu && (
              <motion.div
                layoutId="activeIndicator"
                className="absolute left-0 top-0 bottom-0 w-1 bg-primary-500 rounded-r-full"
                initial={false}
                transition={{ type: "spring", stiffness: 500, damping: 30 }}
              />
            )}
            
            <item.icon 
              className={`
                w-5 h-5 mr-3 transition-colors
                ${isActive ? 'text-primary-500' : 'text-gray-400 group-hover:text-primary-500'}
              `} 
            />
            
            <div className="flex-1">
              <div className={`font-medium ${isActive ? 'text-primary-600' : 'text-gray-700'} flex items-center gap-2`}>
                {item.name}
                {item.badge && (
                  <span className="px-2 py-0.5 text-xs font-semibold text-white bg-primary-500 rounded-full">
                    {item.badge}
                  </span>
                )}
              </div>
              <div className="text-xs text-gray-500 mt-0.5">
                {item.description}
              </div>
            </div>

            {hasSubmenu && (
              <svg
                className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            )}

            {isActive && !hasSubmenu && (
              <div className="w-2 h-2 bg-primary-500 rounded-full opacity-60" />
            )}
          </motion.div>
        </Link>

        {/* Submenu */}
        {hasSubmenu && isExpanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="ml-11 mt-1 space-y-1"
          >
            {item.submenu.map((subItem) => {
              const isSubActive = location.pathname === subItem.href;
              return (
                <Link key={subItem.href} to={subItem.href}>
                  <motion.div
                    whileHover={{ x: 4 }}
                    className={`
                      px-4 py-2 text-sm rounded-lg transition-all
                      ${isSubActive
                        ? 'text-primary-600 bg-primary-50 font-medium'
                        : 'text-gray-600 hover:text-primary-600 hover:bg-gray-50'
                      }
                    `}
                  >
                    {subItem.name}
                  </motion.div>
                </Link>
              );
            })}
          </motion.div>
        )}
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full bg-white">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-6 border-b border-gray-200">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl flex items-center justify-center shadow-lg shadow-primary-500/30">
            <TruckIcon className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-display font-bold text-gray-900">
              Used<span className="text-primary-500">Adda</span>
            </h1>
            <p className="text-xs text-gray-500 font-medium">Admin Panel</p>
          </div>
        </div>
        <button
          onClick={() => setSidebarOpen(false)}
          className="p-2 rounded-lg text-gray-400 hover:text-gray-500 hover:bg-gray-100 lg:hidden transition-colors"
        >
          <XMarkIcon className="w-5 h-5" />
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-4 py-6 space-y-2 overflow-y-auto">
        {navigationItems.map((item) => renderNavItem(item))}
      </nav>

      {/* Footer */}
      <div className="px-6 py-4 border-t border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-gradient-to-br from-primary-500 to-primary-600 rounded-full flex items-center justify-center">
              <span className="text-sm font-medium text-white">A</span>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-900">Admin User</p>
              <p className="text-xs text-gray-500">Super Admin</p>
            </div>
          </div>
          <button className="p-2 text-gray-400 hover:text-gray-500 rounded-lg hover:bg-gray-100 transition-colors">
            <CogIcon className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;